<script setup lang="ts">
import { computed } from 'vue';
import AdpDocumentCardLink from '@/components/common/adp-document/adp-document-card-link.vue';
import { AdpDocumentBasicInfo } from '@/types';
import { imageErrorHandler, noImageByAspectRatio } from '@/utils/errorHandler';

interface Props {
  adpDocument: AdpDocumentBasicInfo;
  variant: 'banner' | 'thumbnail';
  bannerRatio?: '3to1' | '16to9';
  thumbnailSize?: 's' | 'xm' | 'm';
  isRelatedArticle?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  bannerRatio: '3to1',
  thumbnailSize: 'xm',
  isRelatedArticle: false,
});

const emit = defineEmits<{
  'article-link-clicked': [];
  'article-link-context-menu': [event: Event];
}>();

const fallbackRatio = computed(() =>
  props.variant === 'banner' ? props.bannerRatio : '1to1',
);

const imageUrl = computed(() => {
  if (props.adpDocument.image_url) {
    return props.adpDocument.image_url;
  }
  return noImageByAspectRatio[fallbackRatio.value];
});

const bannerAspectRatio = computed(() =>
  props.bannerRatio === '16to9' ? '16 / 9' : '3 / 1',
);
</script>

<template>
  <div v-if="variant === 'banner'" class="document-img-banner">
    <AdpDocumentCardLink
      :adp-document="adpDocument"
      @article-link-clicked="emit('article-link-clicked')"
      @article-link-context-menu="emit('article-link-context-menu', $event)"
    >
      <img
        loading="lazy"
        :src="imageUrl"
        @error="imageErrorHandler($event, fallbackRatio)"
      />
    </AdpDocumentCardLink>
  </div>
  <div
    v-else
    class="document-image-wrap"
    :class="{
      'is-related-article': isRelatedArticle,
      'is-size-s': thumbnailSize === 's',
    }"
  >
    <AdpDocumentCardLink
      class="image-link"
      :adp-document="adpDocument"
      @article-link-clicked="emit('article-link-clicked')"
      @article-link-context-menu="emit('article-link-context-menu', $event)"
    >
      <img
        class="document-image o-article-image"
        :class="{
          'o-alliance-image': adpDocument.is_alliance_media,
          'o-small-image': isRelatedArticle,
          'o-thumbnail-s': thumbnailSize === 's',
        }"
        loading="lazy"
        :src="imageUrl"
        @error="imageErrorHandler($event, fallbackRatio)"
      />
    </AdpDocumentCardLink>
  </div>
</template>

<style lang="scss" scoped>
.document-img-banner {
  img {
    display: block;
    width: 100%;
    aspect-ratio: v-bind(bannerAspectRatio);
    object-fit: cover;
    object-position: 50% 25%;
    border-radius: 4px 4px 0 0;
  }
}

.document-image-wrap {
  margin-right: 2px;

  &.is-related-article {
    align-self: start;
  }

  &.is-size-s {
    margin-right: 0;
  }
}

.o-article-image {
  height: 80px;
  width: 80px;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  object-position: 50% 25%;
  border-radius: 4px;

  &.o-alliance-image {
    object-fit: contain;
    object-position: 0;
  }
  &.o-small-image {
    height: 60px;
    width: 60px;
  }
  &.o-thumbnail-s {
    height: 48px;
    width: 48px;
    border-left: 1px solid $color-gray400;
    box-sizing: border-box;
  }
}
</style>
